import { useState } from "react";
import { Link } from "react-router-dom";
import type { Pkg } from "../pages/builder";
import { LoadingRows, Card, Checkbox, Button } from "./components";
import { PackageTile } from "./PackageTile";

/**
 * „Paket übernehmen"-Tab (Teil von Region 4b) — Paket-Kacheln-Grid. Aus `builder.tsx`
 * (Schritt 6, Einheit B, Konzept § 2.3).
 *
 * `applyPackage()` (per `onApply`-Prop) bleibt beim Parent (`builder.tsx`): es legt eine NEUE
 * Variante an (`POST /api/events/:id/offers`), wendet das Paket darauf an, schreibt in
 * `pendingActivateIdRef`/`pendingSwitchHinweisRef` und stoesst `offersQ.reload()` an —
 * fachlicher State im Sinne von Konzept § 3 Tabelle Zeile 1 (Autosave-/Options-Kette), kein
 * rein lokaler UI-Zustand dieser Karte. Kein H-12-Gate wandert hierher: `applyPackage()`
 * traegt sein eigenes (`if (!id || !editable) return; // H-12`) und bleibt komplett beim
 * Parent, also gibt es hier nichts wegzulassen (Konzept § 6.4 greift nicht auf diese Datei).
 *
 * Phase B (Konzept § 2.3/§ 5): `applyingPkgId` — nur der Spinner-Zustand der Kachel waehrend
 * der Anwendung — ist rein lokaler UI-State dieser Karte und wandert vollstaendig hierher.
 * `applyPackage()` selbst faengt seine Fehler bereits intern ab (`catch`/`setError`), die
 * zurueckgegebene Promise loest also immer normal auf — `finally` hier reicht, um den Spinner
 * in jedem Fall (Erfolg wie Fehlschlag) wieder auszuschalten.
 */
export function PaketUebernehmenPanel({
  aktivePakete,
  loading,
  onApply,
}: {
  aktivePakete: Pkg[];
  loading: boolean;
  onApply: (pkg: Pkg | Pkg[]) => Promise<void>;
}) {
  const [applyingPkgId, setApplyingPkgId] = useState<string | null>(null);
  // Antoine 12.10.2026: mehrere Pakete ankreuzen -> je Paket eine eigene Variante.
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [mehrfachBusy, setMehrfachBusy] = useState(false);
  async function handleApply(pkg: Pkg) {
    setApplyingPkgId(pkg.id);
    try {
      await onApply(pkg);
    } finally {
      setApplyingPkgId(null);
    }
  }
  async function handleApplyGewaehlt() {
    const pakete = aktivePakete.filter((p) => gewaehlt.includes(p.id));
    setMehrfachBusy(true);
    try {
      await onApply(pakete);
      setGewaehlt([]);
    } finally {
      setMehrfachBusy(false);
    }
  }
  const umschalten = (pkgId: string) => setGewaehlt((g) => (g.includes(pkgId) ? g.filter((x) => x !== pkgId) : [...g, pkgId]));
  return (
    <Card className="p-4">
      {loading ? (
        <LoadingRows rows={1} />
      ) : aktivePakete.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant/60">
          Noch keine Pakete hinterlegt – <Link to="/katalog/pakete" className="text-primary hover:underline">im Katalog anlegen</Link>.
        </p>
      ) : (
        <>
          <div className="flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0">
            {aktivePakete.map((pk) => (
              <div key={pk.id} className="relative shrink-0 lg:shrink">
                <PackageTile pkg={pk} busy={mehrfachBusy || applyingPkgId === pk.id} onApply={() => void handleApply(pk)} />
                <Checkbox
                  className="absolute right-2 top-2"
                  checked={gewaehlt.includes(pk.id)}
                  disabled={mehrfachBusy}
                  onChange={() => umschalten(pk.id)}
                  aria-label={`${pk.name} für mehrere Varianten auswählen`}
                />
              </div>
            ))}
          </div>
          {gewaehlt.length > 0 && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <span className="font-body-sm text-body-sm text-on-surface-variant">Je Paket entsteht eine eigene Variante.</span>
              <Button variant="secondary" disabled={mehrfachBusy} onClick={() => setGewaehlt([])}>
                Auswahl aufheben
              </Button>
              <Button disabled={mehrfachBusy} onClick={() => void handleApplyGewaehlt()}>
                {gewaehlt.length === 1 ? "1 Paket übernehmen" : `${gewaehlt.length} Pakete übernehmen`}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
